import React, { useState, useEffect, useRef } from 'react';
import {
  Clock,
  Shield, Scale, Gavel,
  Zap, CheckCircle, ArrowRight, AlertTriangle, CreditCard, Smartphone, Globe, TrendingUp, Users, Target, Lightbulb,
  Award, Star, Play, Download, FileText, Check, DollarSign, Pause, Volume2, VolumeX
} from 'lucide-react';
import Footer from './footer';
import Header from './header';




export default function PayoutProcessLanding() {
  const [animatedStats, setAnimatedStats] = useState({
    processingTime: 0,
    successRate: 0,
    dailyPayouts: 0,
    merchants: 0
  });

  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [animationStep, setAnimationStep] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  const videoRef = useRef(null);
  const sectionRefs = useRef([]);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Animated stats
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOut = 1 - Math.pow(1 - progress, 4);

      setAnimatedStats({
        processingTime: Math.round(15 * easeOut),
        successRate: Number((99.8 * easeOut).toFixed(1)),
        dailyPayouts: Math.round(50000 * easeOut),
        merchants: Math.round(25000 * easeOut)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const businessProblems = [
    {
      icon: <AlertTriangle className="w-8 h-8 text-amber-500" />,
      title: "Manual Payout Processes",
      description: "Time-consuming manual processes leading to delays and human errors",
      impact: "Hours wasted daily on repetitive tasks",
      color: "from-amber-50 to-orange-50",
      border: "border-amber-200"
    },
    {
      icon: <Clock className="w-8 h-8 text-blue-500" />,
      title: "Delayed Settlements",
      description: "Traditional banking delays affecting cash flow and vendor relationships",
      impact: "2-5 days settlement time affecting business operations",
      color: "from-blue-50 to-indigo-50",
      border: "border-blue-200"
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-emerald-500" />,
      title: "High Transaction Failures",
      description: "Poor success rates leading to customer dissatisfaction and revenue loss",
      impact: "Up to 15-20% failure rates in traditional systems",
      color: "from-emerald-50 to-teal-50",
      border: "border-emerald-200"
    },
    {
      icon: <FileText className="w-8 h-8 text-violet-500" />,
      title: "Limited Visibility",
      description: "Lack of real-time tracking and comprehensive reporting capabilities",
      impact: "No transparency in transaction status and reconciliation",
      color: "from-violet-50 to-purple-50",
      border: "border-violet-200"
    }
  ];

  const processSteps = [
    {
      step: "01",
      title: "Instant Setup",
      description: "Quick onboarding with minimal documentation required",
      icon: <Zap className="w-7 h-7 text-blue-600" />,
      color: "from-blue-50 via-blue-50 to-indigo-50",
      border: "border-blue-200",
      accent: "text-blue-600"
    },
    {
      step: "02",
      title: "Smart Processing",
      description: "AI-powered routing ensures fastest payout delivery",
      icon: <Shield className="w-7 h-7 text-emerald-600" />,
      color: "from-emerald-50 via-emerald-50 to-teal-50",
      border: "border-emerald-200",
      accent: "text-emerald-600"
    },
    {
      step: "03",
      title: "Real-time Tracking",
      description: "Monitor every payout with complete transparency",
      icon: <Clock className="w-7 h-7 text-violet-600" />,
      color: "from-violet-50 via-violet-50 to-purple-50",
      border: "border-violet-200",
      accent: "text-violet-600"
    },
    {
      step: "04",
      title: "Instant Settlement",
      description: "Funds settled within minutes, not days",
      icon: <CheckCircle className="w-7 h-7 text-orange-600" />,
      color: "from-orange-50 via-orange-50 to-amber-50",
      border: "border-orange-200",
      accent: "text-orange-600"
    }
  ];

  const [stats, setStats] = useState([
    { value: 0, suffix: 'M+', label: 'Transactions', target: 2.5 },
    { value: 0, suffix: 's', label: 'Avg. Speed', target: 3 },
    { value: 0, suffix: '+', label: 'Banks', target: 250 },
    { value: 0, suffix: '%', label: 'Uptime', target: 99.9 }
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prevStats =>
        prevStats.map(stat => ({
          ...stat,
          value: Math.min(stat.value + (stat.target / 100), stat.target)
        }))
      );
    }, 50);

    const animationInterval = setInterval(() => {
      setAnimationStep(prev => (prev + 1) % 4);
    }, 2000);

    return () => {
      clearInterval(interval);
      clearInterval(animationInterval);
    };
  }, []);

  const featuress = [
    { icon: Zap, title: "Instant Processing", desc: "Process payments in under 3 seconds", color: "from-blue-500 to-indigo-500" },
    { icon: Shield, title: "Bank-Grade Security", desc: "End-to-end encryption & compliance", color: "from-emerald-500 to-teal-500" },
    { icon: TrendingUp, title: "Real-time Analytics", desc: "Live dashboard with insights", color: "from-violet-500 to-purple-500" },
    { icon: Clock, title: "24/7 Support", desc: "Round-the-clock assistance", color: "from-orange-500 to-amber-500" }
  ];

  const competitiveAdvantages = [
    {
      icon: <Target className="w-7 h-7" />,
      title: 'Highest Success Rates',
      description: '99.8% success rate vs industry average of 85% through intelligent routing',
      metric: '99.8% Success Rate',
      color: 'from-blue-500/10 to-indigo-500/10',
      border: 'border-blue-200/50',
      iconColor: 'text-blue-600'
    },
    {
      icon: <Clock className="w-7 h-7" />,
      title: 'Fastest Settlement',
      description: '15-second processing vs 2-5 days in traditional banking systems',
      metric: '15 Second Processing',
      color: 'from-emerald-500/10 to-teal-500/10',
      border: 'border-emerald-200/50',
      iconColor: 'text-emerald-600'
    },
    {
      icon: <DollarSign className="w-7 h-7" />,
      title: 'Competitive Pricing',
      description: 'Transparent pricing with no hidden charges and volume-based discounts',
      metric: 'Up to 40% Cost Savings',
      color: 'from-amber-500/10 to-orange-500/10',
      border: 'border-amber-200/50',
      iconColor: 'text-amber-600'
    },
    {
      icon: <Shield className="w-7 h-7" />,
      title: 'Enterprise Grade Security',
      description: 'Bank-grade security with PCI DSS compliance and real-time fraud detection',
      metric: '100% Secure',
      color: 'from-violet-500/10 to-purple-500/10',
      border: 'border-violet-200/50',
      iconColor: 'text-violet-600'
    },
    {
      icon: <Lightbulb className="w-7 h-7" />,
      title: 'Smart Technology',
      description: 'AI-powered routing, predictive analytics, and automated reconciliation',
      metric: 'AI-Powered Platform',
      color: 'from-rose-500/10 to-pink-500/10',
      border: 'border-rose-200/50',
      iconColor: 'text-rose-600'
    },
    {
      icon: <Users className="w-7 h-7" />,
      title: 'Dedicated Support',
      description: 'Personal relationship manager with 24/7 technical support and training',
      metric: '24/7 Expert Support',
      color: 'from-cyan-500/10 to-blue-500/10',
      border: 'border-cyan-200/50',
      iconColor: 'text-cyan-600'
    }
  ];
 useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.12 }
    );
    sectionRefs.current.forEach((ref) => ref && observer.observe(ref));
    return () => observer.disconnect();
  }, []);
  const reveal = (id, base = "") =>
    `${base} transition-all duration-700 ease-out will-change-transform will-change-opacity ${
      isVisible[id] ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-[0.98]'
    } motion-reduce:transition-none motion-reduce:translate-y-0 motion-reduce:opacity-100`;

  const settlements = [
    {
      id: 1,
      title: "File-Based & App-Based Settlements",
      description: "Modern digital settlement processes that streamline legal proceedings through electronic filing and mobile applications. These settlements offer convenience and accessibility for all parties involved.",
      icon: <FileText className="w-8 h-8 text-white" />,
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop",
      color: "from-blue-600 to-indigo-600"
    },
    {
      id: 2,
      title: "Group Settlements",
      description: "Collaborative resolution processes involving multiple parties working together to reach mutually beneficial agreements. These settlements often involve class actions and collective bargaining scenarios.",
      icon: <Users className="w-8 h-8 text-white" />,
      image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=600&h=400&fit=crop",
      color: "from-emerald-600 to-teal-600"
    },
    {
      id: 3,
      title: "Revenue Settlements",
      description: "Financial agreements focused on revenue distribution and monetary compensation. These settlements address financial disputes and ensure fair distribution of assets and earnings among parties.",
      icon: <TrendingUp className="w-8 h-8 text-white" />,
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=400&fit=crop",
      color: "from-amber-600 to-orange-600"
    },
   
    {
      id: 4,
      title: "Split Settlements",
      description: "Divided settlement structures that allow for proportional distribution of outcomes among multiple beneficiaries. These settlements provide flexibility in complex multi-party agreements.",
      icon: <Gavel className="w-8 h-8 text-white" />,
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=400&fit=crop",
      color: "from-rose-600 to-pink-600"
    }
  ];

  const anvitechSolutions = [
    {
      title: "Automated Bulk Payouts",
      description: "Process thousands of transactions simultaneously with intelligent queue management and batch processing capabilities",
      icon: <Users className="w-8 h-8 text-blue-600" />,
      benefits: ["Bulk Processing", "CSV Upload", "API Integration", "Scheduled Payouts"],
      roi: "Save 80% processing time",
      color: "from-blue-50 to-indigo-50",
      border: "border-blue-200"
    },
    {
      title: "Multi-Banking Network",
      description: "Connected to 50+ banks and financial institutions ensuring highest success rates and optimal routing",
      icon: <Globe className="w-8 h-8 text-emerald-600" />,
      benefits: ["10+ Bank Partnerships", "Smart Routing", "Failover Protection", "24/7 Uptime"],
      roi: "99.8% transaction success rate",
      color: "from-emerald-50 to-teal-50",
      border: "border-emerald-200"
    },
    {
      title: "Real-Time Dashboard",
      description: "Comprehensive analytics and reporting with live transaction monitoring and instant notifications",
      icon: <TrendingUp className="w-8 h-8 text-violet-600" />,
      benefits: ["Live Tracking", "Advanced Analytics", "Custom Reports", "API Webhooks"],
      roi: "Complete transaction visibility",
      color: "from-violet-50 to-purple-50",
      border: "border-violet-200"
    },
    {
      title: "Compliance & Security",
      description: "Bank-grade security with full regulatory compliance and fraud detection mechanisms",
      icon: <Shield className="w-8 h-8 text-orange-600" />,
      benefits: ["RBI Compliant", "256-bit Encryption", "Fraud Detection", "PCI DSS Certified"],
      roi: "100% secure transactions",
      color: "from-orange-50 to-amber-50",
      border: "border-orange-200"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Keep unchanged as requested */}
      <Header />
      <section
        className="relative pt-20 pb-16 px-4 sm:px-6 lg:px-8 min-h-screen flex items-center overflow-hidden"
        style={{
          background: `linear-gradient(135deg, 
            rgba(59, 130, 246, 0.1) 0%, 
            rgba(147, 51, 234, 0.1) 50%, 
            rgba(219, 39, 119, 0.1) 100%
          )`
        }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-blue-200/30 to-purple-200/30 rounded-full animate-pulse transform rotate-45"
            style={{ transform: `translateY(${scrollY * 0.1}px) rotate(45deg)` }}
          ></div>
          <div
            className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-indigo-200/40 to-blue-200/40 rounded-lg animate-bounce transform rotate-12"
            style={{ animationDelay: '1s', transform: `translateY(${scrollY * 0.15}px) rotate(12deg)` }}
          ></div>
          <div
            className="absolute bottom-20 left-20 w-16 h-16 bg-gradient-to-br from-purple-200/30 to-pink-200/30 rounded-full animate-pulse transform -rotate-12"
            style={{ animationDelay: '2s', transform: `translateY(${scrollY * 0.2}px) rotate(-12deg)` }}
          ></div>
          <div
            className="absolute bottom-40 right-10 w-20 h-20 bg-gradient-to-br from-cyan-200/40 to-blue-200/40 rounded-lg animate-bounce transform rotate-45"
            style={{ animationDelay: '0.5s', transform: `translateY(${scrollY * 0.12}px) rotate(45deg)` }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-full text-sm font-medium shadow-lg animate-bounce">
                  <Star className="w-4 h-4" />
                  India's Fastest Payout Solution
                </div>

                <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                  <span className="text-gray-800">Lightning</span>
                  <span className="block bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent animate-pulse">
                    Fast Payouts
                  </span>
                  <span className="text-gray-800">Made Simple</span>
                </h1>

                <p className="text-xl text-gray-600 leading-relaxed">
                  Experience unmatched speed and reliability with our advanced payout infrastructure.
                  Process payments in seconds, not hours.
                </p>
              </div>

              {/* Feature Cards */}
              <div className="grid grid-cols-2 gap-4">
                {featuress.map((feature, index) => (
                  <div
                    key={index}
                    className={`bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-white/60 transform transition-all duration-500 hover:scale-105 hover:shadow-xl ${animationStep === index ? 'scale-105 shadow-xl ring-2 ring-blue-400/50' : ''
                      }`}
                    style={{
                      animationDelay: `${index * 0.2}s`,
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 bg-gradient-to-br ${feature.color} rounded-lg shadow-md`}>
                        <feature.icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800 text-sm">{feature.title}</h3>
                        <p className="text-xs text-gray-600 mt-1">{feature.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Content - Video Player - Keep unchanged as requested */}
            <div className="relative">
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-4 shadow-2xl border border-white/60 relative overflow-hidden max-w-md mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>

                <div className="relative z-10 space-y-3">
                  {/* Video Player */}
                  <div className="relative rounded-xl overflow-hidden shadow-lg">
                    <video
                      ref={videoRef}
                      className="w-full h-auto"
                      muted
                      loop
                      autoPlay
                      playsInline
                      poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3CRect width='400' height='300' fill='%23f3f4f6'/%3E%3Ctext x='200' y='150' text-anchor='middle' font-family='Arial' font-size='16' fill='%236b7280'%3EVideo Preview%3C/text%3E%3C/svg%3E"
                    >
                      <source src="/bankgram.mp4" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>
              </div>

              {/* Floating Success Card */}
              <div className="absolute -top-6 -right-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-2xl p-4 shadow-xl transform hover:scale-110 transition-transform duration-300 animate-bounce">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-6 h-6" />
                  <div>
                    <div className="font-bold text-lg">99.8%</div>
                    <div className="text-sm opacity-90">Success Rate</div>
                  </div>
                </div>
              </div>

              {/* Floating Transaction Card */}
              <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl p-3 shadow-lg transform hover:scale-110 transition-transform duration-300 animate-pulse">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  <div>
                    <div className="font-bold text-sm">₹2.5M+</div>
                    <div className="text-xs opacity-90">Processed Today</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Problems Section - Enhanced */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full mb-6">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-4xl font-bold mb-6 text-gray-900">
              Are These Payout Challenges
              <span className="block bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                Hurting Your Business?
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Manual processes, delays, and high failure rates are costing businesses millions in lost revenue and operational efficiency.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {businessProblems.map((problem, index) => (
              <div 
                key={index} 
                className={`bg-gradient-to-br ${problem.color} rounded-2xl p-8 shadow-lg border-2 ${problem.border} hover:shadow-2xl hover:scale-105 transition-all duration-500 group`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="text-center space-y-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full shadow-lg group-hover:scale-110 transition-transform duration-300">
                    {problem.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{problem.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{problem.description}</p>
                  <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-white/50">
                    <p className="text-gray-800 text-sm font-semibold">{problem.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Anvitech Solutions Section - Enhanced */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full mb-6">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-4xl font-bold mb-6 text-gray-900">
              How Anvitech Solves Your
              <span className="block bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                Payout Challenges
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our comprehensive payout platform addresses every pain point with cutting-edge technology
              and industry-leading performance metrics.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {anvitechSolutions.map((solution, index) => (
              <div key={index} className="group h-full">
                <div className={`bg-gradient-to-br ${solution.color} rounded-3xl p-8 border-2 ${solution.border} hover:shadow-2xl hover:scale-105 transition-all duration-500 h-full`}>
                  <div className="flex items-start gap-6 mb-6">
                    <div className="p-4 bg-white rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                      {solution.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold mb-3 text-gray-900">{solution.title}</h3>
                      <div className="bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full inline-block shadow-sm">
                        <span className="text-gray-800 text-sm font-semibold">{solution.roi}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-8 leading-relaxed text-lg">{solution.description}</p>
                  <div className="grid grid-cols-2 gap-4">
                    {solution.benefits?.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-center gap-3 text-gray-700 bg-white/60 backdrop-blur-sm rounded-lg p-3 hover:bg-white/80 transition-all duration-300">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <span className="font-medium">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Enhanced */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full mb-6">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              How Our Payout Process Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Simple, secure, and lightning-fast payout processing in just 4 easy steps
            </p>
          </div>

          <div className="grid lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative group">
                <div className={`bg-gradient-to-br ${step.color} rounded-3xl p-8 border-2 ${step.border} hover:shadow-2xl hover:scale-105 transition-all duration-500 h-full`}>
                  <div className="text-center space-y-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                      {step.icon}
                    </div>

                    <div className={`text-4xl font-bold ${step.accent} opacity-60`}>
                      {step.step}
                    </div>

                    <h3 className="text-2xl font-bold text-gray-900">
                      {step.title}
                    </h3>

                    <p className="text-gray-700 leading-relaxed text-lg">
                      {step.description}
                    </p>
                  </div>
                </div>

                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <div className="bg-white rounded-full p-2 shadow-lg">
                      <ArrowRight className="w-6 h-6 text-gray-400" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Competitive Advantages - Enhanced */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-violet-600 to-purple-600 rounded-full mb-6">
              <Award className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-4xl font-bold mb-6 text-gray-900">
              Why  Anvitech ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Industry-leading performance metrics and exclusive features that give your business the competitive edge.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {competitiveAdvantages.map((advantage, index) => (
              <div key={index} className="group h-full">
                <div className={`bg-gradient-to-br ${advantage.color} backdrop-blur-sm rounded-2xl p-8 border-2 ${advantage.border} hover:shadow-2xl hover:scale-105 transition-all duration-500 h-full`}>
                  <div className="flex items-start gap-4 mb-6">
                    <div className={`${advantage.iconColor} group-hover:scale-110 transition-transform duration-300 bg-white p-3 rounded-xl shadow-sm`}>
                      {advantage.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-3 text-gray-900">{advantage.title}</h3>
                      <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full inline-block shadow-sm">
                        <span className="text-gray-800 text-sm font-semibold">{advantage.metric}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{advantage.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Settlement Solutions - Enhanced */}
    <section className=" bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 py-16 px-4">
           <div className="max-w-7xl mx-auto" id="settlements" ref={el => (sectionRefs.current[22] = el)}>
             <header className={reveal('settlements', "text-center mb-16")}>
               <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-slate-600 to-slate-700 rounded-full mb-6">
                 <Scale className="w-8 h-8 text-white" />
               </div>
               <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                 Legal Settlement Solutions
               </h1>
               <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
                 Comprehensive settlement services designed to resolve disputes efficiently and effectively. Our modern approach combines traditional legal expertise with innovative digital solutions.
               </p>
             </header>
   
             <div className="space-y-12">
               {settlements.map((settlement, index) => (
                 <div
                   key={settlement.id}
                   className={reveal(`set-${index}`, `flex flex-col lg:flex-row items-center gap-8 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`)}
                   id={`set-${index}`}
                   ref={el => (sectionRefs.current[23 + index] = el)}
                 >
                   {/* Content Side */}
                   <div className="flex-1 space-y-6">
                     <div className="flex items-center gap-4">
                       <div className={`p-3 bg-gradient-to-r ${settlement.color} rounded-xl shadow-sm`}>
                         {settlement.icon}
                       </div>
                       <h2 className="text-3xl font-bold text-white">
                         {settlement.title}
                       </h2>
                     </div>
                     <p className="text-lg text-slate-300 leading-relaxed">
                       {settlement.description}
                     </p>
                   </div>
   
                   {/* Visual Side */}
                   <div className="flex-1 max-w-md">
                     <div className="relative overflow-hidden rounded-xl shadow-md border border-slate-700 bg-slate-800/60 backdrop-blur-sm">
                       <img
                         src={settlement.image}
                         alt={settlement.title}
                         className="w-full h-64 object-cover"
                       />
                       <div className="p-6">
                         <div className="flex items-center justify-between mb-4">
                           <h3 className="text-lg font-semibold text-white">{settlement.title}</h3>
                           <div className="w-2 h-2 bg-emerald-400 rounded-full" />
                         </div>
                         <div className="grid grid-cols-2 gap-3">
                           <div className="text-center p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                             <div className="text-xl font-bold text-white">98%</div>
                             <div className="text-slate-300 text-xs">Success Rate</div>
                           </div>
                           <div className="text-center p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                             <div className="text-xl font-bold text-white">24/7</div>
                             <div className="text-slate-300 text-xs">Support</div>
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>
               ))}
             </div>
           </div>
         </section>

      {/* Trust Indicators - Enhanced */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full mb-6">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-4xl font-bold text-gray-900 mb-6">
              Secure & Compliant
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Bank-grade security with industry-leading compliance standards ensuring your transactions are always protected
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                icon: <Shield className="w-10 h-10 text-emerald-600" />, 
                title: "256 bit", 
                subtitle: "Encryption",
                color: "from-emerald-50 to-teal-50",
                border: "border-emerald-200"
              },
              { 
                icon: <Award className="w-10 h-10 text-blue-600" />, 
                title: "Bank Security", 
                subtitle: "Certified",
                color: "from-blue-50 to-indigo-50",
                border: "border-blue-200"
              },
              { 
                icon: <CheckCircle className="w-10 h-10 text-violet-600" />, 
                title: "VAPT", 
                subtitle: "Certified",
                color: "from-violet-50 to-purple-50",
                border: "border-violet-200"
              },
              { 
                icon: <Globe className="w-10 h-10 text-orange-600" />, 
                title: "24/7 Security", 
                subtitle: "Monitoring",
                color: "from-orange-50 to-amber-50",
                border: "border-orange-200"
              }
            ].map((item, index) => (
              <div 
                key={index} 
                className={`text-center p-8 bg-gradient-to-br ${item.color} rounded-2xl border-2 ${item.border} hover:shadow-xl hover:scale-105 transition-all duration-500 group`}
              >
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-lg mb-6 group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-gray-700 font-medium">{item.subtitle}</p>
              </div>
            ))}
          </div>

          {/* Stats Section */}
         
        </div>
      </section>

  

      {/* Book a Demo CTA Section */}
     <section className="py-8 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-white/5 rounded-lg animate-bounce transform rotate-45" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-20 w-16 h-16 bg-white/10 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-40 right-10 w-20 h-20 bg-white/5 rounded-lg animate-bounce transform -rotate-12" style={{ animationDelay: '0.5s' }}></div>
        
        <div className="absolute top-1/4 left-1/4 animate-float">
          <Zap className="w-8 h-8 text-white/20" />
        </div>
        <div className="absolute top-1/3 right-1/3 animate-float" style={{ animationDelay: '1.5s' }}>
          <Shield className="w-6 h-6 text-white/20" />
        </div>
        <div className="absolute bottom-1/3 left-1/3 animate-float" style={{ animationDelay: '3s' }}>
          <TrendingUp className="w-7 h-7 text-white/20" />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
        {/* Main Content */}
        <div className="space-y-4 animate-fadeInUp">
         
          
          <h2 className="text-3xl lg:text-5xl font-bold text-white leading-tight animate-slideInLeft">
            Ready to Experience
            <span className="block bg-gradient-to-r from-yellow-300 via-orange-300 to-pink-300 bg-clip-text text-transparent animate-pulse">
              Lightning Fast Payouts?
            </span>
          </h2>
        </div>

        {/* CTA Button - Smaller and More Compact */}
        <div className="mt-6 animate-fadeInUp" style={{ animationDelay: '1.2s' }}>
          <button 
            className="group relative inline-flex items-center gap-3 bg-gradient-to-r from-white to-blue-50 text-blue-700 px-8 py-3 rounded-xl font-semibold text-lg hover:from-yellow-300 hover:to-orange-300 hover:text-gray-900 transform hover:scale-105 transition-all duration-500 shadow-xl hover:shadow-yellow-300/50"
             onClick={() => {
              window.location.href = '/contact';
            }}
          >
            <Play className="w-5 h-5 group-hover:animate-bounce" />
            <span>Get In Touch</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            
            {/* Button Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(5deg); }
          66% { transform: translateY(5px) rotate(-5deg); }
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-50px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(50px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slideInLeft {
          animation: slideInLeft 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slideInRight {
          animation: slideInRight 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slideInUp {
          animation: slideInUp 0.8s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </section>
 



      <Footer />
    </div>
  );
}

